import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '@/services';
import type { AuditLogEntry } from '@/types';
import { notifyLiveDataError } from '@/utils';

/** Only the most recent entries are shown — enough for practical review without unbounded reads. */
const AUDIT_LOG_PAGE_SIZE = 200;

/** Subscribes to the most recent admin action audit log entries. Admin-only (enforced by rules). */
export const useAuditLog = () => {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'auditLog'),
      orderBy('timestamp', 'desc'),
      limit(AUDIT_LOG_PAGE_SIZE),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setEntries(
          snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as AuditLogEntry),
        );
        setLoading(false);
      },
      (error) => {
        // Without this, a query failure (e.g. a stale ID token not yet
        // carrying the admin claim) leaves `loading` stuck at true forever,
        // showing skeleton rows indefinitely instead of surfacing anything.
        notifyLiveDataError('useAuditLog', error);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  return { entries, loading };
};
